import {
  WSElement,
  WSFlexBox,
  WSGrid,
  WSList,
  WSPage,
  WSPageToolbar,
  WSPanel,
  WSSectionToolbar
} from "@wingspanhq/fe-component-library";
import React from "react";
import { RouteComponentProps } from "react-router-dom";
import { queriesPayeeEngagements, queriesPayees } from "@query";
import { useXWingspanPayerId } from "../useXWingspanPayerId";
import { WSQueries } from "../../../../../query/WSQuery";
import { FieldView } from "../../../../../shared/components/FieldView/FieldView";
import { ResourceHistory } from "../../../../../shared/components/ResourceHistory";
import { usePayeeEngagementHistoryResources } from "../../../hooks/usePayeeEngagementHistoryResources";
import { selectorCounterpartyDisplayName } from "../../../../../shared/selectors/selectorCounterpartyDisplayName";
import {
  PAYEES_ROOT_PATH,
  useGoToEngagementDetails,
  useGoToPayeeDetails
} from "../../../utils";

export const RoutePayeeEngagementHistory: React.FC<
  RouteComponentProps<{ payeeId: string; engagementId: string }>
> = ({
  match: {
    params: { payeeId, engagementId }
  },
  history
}) => {
  const xWingspanAccount = useXWingspanPayerId();
  // The search row carries the server-resolved contractor name this breadcrumb
  // needs, so it replaces the `Payee` read outright.
  // Direct `getPayee`, not the `searchPayeeRows` projection. The search index is
  // currently unstable, and this screen gates its whole render on the read, so a
  // failing index took the page down with it. The trade is the displayed name:
  // the row carries the server-resolved `profile.resolvedName`, the entity does
  // not, so `selectorCounterpartyDisplayName` falls back through
  // displayName -> company DBA -> email -> id.
  const queryPayee = queriesPayees.getPayee.useQuery({
    payeeId,
    xWingspanAccount
  });
  const queryEngagement = queriesPayeeEngagements.getPayeeEngagement.useQuery({
    payeeId,
    payeeEngagementId: engagementId,
    xWingspanAccount
  });
  const goToPayeeDetails = useGoToPayeeDetails();
  const goToEngagementDetails = useGoToEngagementDetails();
  const historyResources = usePayeeEngagementHistoryResources(engagementId);

  return (
    <WSPage>
      <WSQueries queries={{ queryPayee, queryEngagement }}>
        {({ queryPayeeData, queryEngagementData: engagement }) => {
          // v1 `selectorPayerPayeeEngagementName(_, true)` reduced to `engagementName`;
          // the selector stays on v1 for the payer-engagement + search-row callers.
          const engagementName = engagement.engagementName ?? undefined;

          return (
            <>
              <WSPageToolbar
                title="Assignment history"
                breadcrumbs={[
                  {
                    label: "Contractors",
                    onClick: () => {
                      history.push(PAYEES_ROOT_PATH);
                    }
                  },
                  {
                    label: selectorCounterpartyDisplayName(queryPayeeData),
                    onClick: () => {
                      goToPayeeDetails(payeeId);
                    }
                  },
                  {
                    label: engagementName || "Assignment",
                    onClick: () => {
                      goToEngagementDetails(payeeId, engagementId);
                    }
                  },
                  {
                    label: "Assignment history"
                  }
                ]}
              />

              <WSGrid>
                <WSGrid.Item span={{ l: "8" }}>
                  <WSList gap="2XL">
                    <WSElement>
                      <WSElement>
                        <WSSectionToolbar title="Assignment information" />
                        <WSPanel>
                          <WSFlexBox gap="XL">
                            <FieldView
                              label="Engagement name"
                              value={engagementName}
                            />
                          </WSFlexBox>
                        </WSPanel>
                      </WSElement>
                    </WSElement>

                    <ResourceHistory
                      resources={historyResources}
                      xWingspanAccount={xWingspanAccount}
                    />
                  </WSList>
                </WSGrid.Item>
              </WSGrid>
            </>
          );
        }}
      </WSQueries>
    </WSPage>
  );
};
